import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useSearchParams,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { useTranslation } from "react-i18next";
import { AuthProvider } from "./contexts/AuthContext";
import { CurrencyProvider } from "./contexts/CurrencyContext";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import Layout from "./components/layout/Layout";
import Dashboard from "./pages/Dashboard";
import Deposit from "./pages/Deposit";
import History from "./pages/History";
import Balance from "./pages/Balance";
import DealerActivation from "./pages/DealerActivation";
import TokenAuth from "./components/auth/TokenAuth";
import ActivationSuccess from "./pages/ActivationSuccess";
import ActivationFailed from "./pages/ActivationFailed";
import PaymentFailed from "./pages/PaymentFailed";
import DepositSuccess from "./pages/DepositSuccess";
import "./i18n/config";

// Initialize Stripe
const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || "",
);

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Component to handle language detection from URL
function LanguageDetector() {
  const [searchParams] = useSearchParams();
  const { i18n } = useTranslation();

  useEffect(() => {
    const langParam = searchParams.get("lang");
    if (langParam) {
      const supportedLanguages = ["en", "de", "it", "el"];
      if (supportedLanguages.includes(langParam)) {
        i18n.changeLanguage(langParam);
        localStorage.setItem("preferredLanguage", langParam);
      }
    } else {
      // Check if there's a saved language preference
      const savedLang = localStorage.getItem("preferredLanguage");
      if (savedLang) {
        i18n.changeLanguage(savedLang);
      }
    }
  }, [searchParams, i18n]);

  return null;
}

function App() {
  const { i18n } = useTranslation();
  const [stripeLocale, setStripeLocale] = useState<
    "auto" | "en" | "de" | "it" | "el"
  >("en");

  // Early locale from URL (before LanguageDetector runs)
  useEffect(() => {
    const urlLang = new URLSearchParams(window.location.search).get("lang") as
      | "en"
      | "de"
      | "it"
      | "el"
      | null;
    if (urlLang) {
      const map: Record<string, "en" | "de" | "it" | "el"> = {
        en: "en",
        de: "de",
        it: "it",
        el: "el",
      };
      setStripeLocale(map[urlLang] || "en");
    }
  }, []);

  useEffect(() => {
    const lang = (i18n.language ||
      localStorage.getItem("preferredLanguage") ||
      "en") as "en" | "de" | "it" | "el";
    const map: Record<string, "en" | "de" | "it" | "el"> = {
      en: "en",
      de: "de",
      it: "it",
      el: "el",
    };
    setStripeLocale(map[lang] || "en");
  }, [i18n.language]);
  // Remove dark mode functionality - always use light theme
  useEffect(() => {
    document.documentElement.classList.remove("dark");
    localStorage.setItem("theme", "light");
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Elements
        key={stripeLocale}
        stripe={stripePromise}
        options={{ locale: stripeLocale }}
      >
        <AuthProvider>
          <CurrencyProvider>
          <Router>
            <LanguageDetector />
            <Routes>
              {/* Auth routes */}
              <Route path="/auth" element={<TokenAuth />} />
              <Route path="/login" element={<TokenAuth />} />

              {/* Protected routes */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Dashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/deposit"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Deposit />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/history"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <History />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/balance"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Balance />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dealer/activation"
                element={
                  <ProtectedRoute>
                    <DealerActivation />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/activation-success"
                element={
                  <ProtectedRoute>
                    <ActivationSuccess />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/activation-failed"
                element={
                  <ProtectedRoute>
                    <ActivationFailed />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payment-failed"
                element={
                  <ProtectedRoute>
                    <PaymentFailed />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/deposit-success"
                element={
                  <ProtectedRoute>
                    <DepositSuccess />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Router>

          {/* Toast notifications */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: "#ffffff",
                color: "#404e5a",
                border: "1px solid #e5e7eb",
                boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: "#17B1C6",
                  secondary: "#fff",
                },
              },
              error: {
                duration: 5000,
                iconTheme: {
                  primary: "#EF4444",
                  secondary: "#fff",
                },
              },
            }}
          />
          </CurrencyProvider>
        </AuthProvider>
      </Elements>
    </QueryClientProvider>
  );
}

export default App;
